import type { ConfirmModalFactory, IProps } from "./confirm-modal.types";
import { componentWrapper } from "../../utilities";
import { Modal } from "../modal";
import { Stack } from "../stack";
import { Text } from "../text";
import { useCallback, useMemo, useState } from "react";

const ConfirmModal = componentWrapper<ConfirmModalFactory, IProps>(
    "ConfirmModal",
    (
        {
            title,
            primaryButtonText,
            secondaryButtonText,
            description,
            explanation,
            primaryButtonProps,
            secondaryButtonProps,
            hideSecondaryButton,
            closeOnClickOutside,
            closeOnEscape,
            loading,
            onConfirm,
            onCancel,
            onClose,
            ...otherProps
        },
        ref
    ) => {
        const [isInternalLoading, setIsInternalLoading] = useState(false);

        const isLoading = useMemo(() => loading ?? isInternalLoading, [loading, isInternalLoading]);

        const handleConfirm = useCallback(() => {
            try {
                const result = onConfirm();

                // If result is a thenable (Promise-like), show loading state until it settles
                if (result && typeof result.then === "function") {
                    setIsInternalLoading(true);
                    return Promise.resolve(result)
                        .then((res) => {
                            setIsInternalLoading(false);
                            return res;
                        })
                        .catch((err) => {
                            setIsInternalLoading(false);
                            throw err;
                        });
                }

                // Synchronous result: return it directly, no loading state
                return result;
            } catch (err) {
                // If onConfirm throws synchronously, just rethrow
                throw err;
            }
        }, [onConfirm]);

        return (
            <Modal.Root
                size="md"
                variant="confirm"
                onClose={isLoading ? () => {} : onClose}
                closeOnClickOutside={closeOnClickOutside ?? !isLoading}
                closeOnEscape={closeOnEscape ?? !isLoading}
                {...otherProps}
                ref={ref}
            >
                <Modal.Overlay />
                <Modal.Content>
                    <Modal.Header>
                        <Modal.Title>{title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Stack>
                            {description && <Text size="sm">{description}</Text>}
                            {explanation && (
                                <Text size="sm" dimmed>
                                    {explanation}
                                </Text>
                            )}
                        </Stack>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.Footer.Actions>
                            {!hideSecondaryButton && (
                                <Modal.Footer.Actions.Cancel
                                    onClick={onCancel ?? onClose}
                                    disabled={isLoading}
                                    text={secondaryButtonText}
                                    data-testid="confirm-cancel-button"
                                    {...secondaryButtonProps}
                                />
                            )}
                            <Modal.Footer.Actions.Remove
                                onClick={handleConfirm}
                                text={primaryButtonText}
                                loading={isLoading}
                                data-testid="confirm-remove-button"
                                {...primaryButtonProps}
                            />
                        </Modal.Footer.Actions>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Root>
        );
    }
);

export { ConfirmModal };
